// Shared Product / Offer structured-data builders.
//
// Rules (Google Merchant listing + Product rich results):
//  * Never fabricate data. aggregateRating / review are only emitted when
//    genuine, product-level values are passed in.
//  * gtin / mpn are only emitted for real global identifiers — a grading
//    certificate number is NOT a GTIN and must never be sent as one.
//  * Every Offer carries price, priceCurrency, availability, validFrom,
//    shippingDetails and hasMerchantReturnPolicy.

import { SITE_URL, SITE_NAME } from "@/lib/seo";
import { SHIPPING_OPTIONS } from "@/lib/shipping";

/** Default buyer-facing UK postage used for OfferShippingDetails. */
const DEFAULT_SHIPPING_ID = "royal_mail_tracked_48";

function defaultShippingPence(): number {
  return SHIPPING_OPTIONS.find((o) => o.id === DEFAULT_SHIPPING_ID)?.costPence ?? 385;
}

/**
 * Organization-level shipping policy, expressed as OfferShippingDetails.
 * Mirrors the flat-rate Royal Mail Tracked 48 service quoted at checkout.
 */
export function offerShippingDetails() {
  return {
    "@type": "OfferShippingDetails",
    shippingRate: {
      "@type": "MonetaryAmount",
      value: (defaultShippingPence() / 100).toFixed(2),
      currency: "GBP",
    },
    shippingDestination: {
      "@type": "DefinedRegion",
      addressCountry: "GB",
    },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      handlingTime: {
        "@type": "QuantitativeValue",
        minValue: 1,
        maxValue: 3,
        unitCode: "DAY",
      },
      transitTime: {
        "@type": "QuantitativeValue",
        minValue: 2,
        maxValue: 3,
        unitCode: "DAY",
      },
    },
  };
}

/**
 * MerchantReturnPolicy matching the published Refund & Dispute Policy:
 * 7 days from delivery to raise a dispute, returns shipped tracked at the
 * seller's expense when a refund is granted.
 */
export function merchantReturnPolicy() {
  return {
    "@type": "MerchantReturnPolicy",
    name: "BidNova Auctions Refund & Dispute Policy",
    url: `${SITE_URL}/legal/refunds`,
    applicableCountry: "GB",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 7,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/FreeReturn",
  };
}

export type ProductRating = {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
};

export type ProductReview = {
  author: string;
  ratingValue: number;
  body?: string | null;
  datePublished?: string | null;
};

export type BuildProductLdInput = {
  url: string;
  name: string;
  description: string;
  image?: string | null;
  images?: string[] | null;
  category?: string | null;
  brand?: string | null;
  /** Internal stock keeping unit — the listing id. */
  sku?: string | null;
  /** Real manufacturer part number only. */
  mpn?: string | null;
  /** Real GTIN (8/12/13/14 digits) only. */
  gtin?: string | null;
  price: string;
  priceCurrency?: string;
  availability: string;
  /** schema.org condition URL; defaults to UsedCondition. */
  itemCondition?: string;
  /** ISO date the offer became valid (listing start). */
  validFrom?: string | null;
  priceValidUntil?: string | null;
  sellerName?: string | null;
  aggregateRating?: ProductRating | null;
  reviews?: ProductReview[] | null;
  extra?: Record<string, unknown>;
};

const GTIN_RE = /^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/;

/** Only returns a GTIN when the value is a structurally valid one. */
export function safeGtin(value: string | null | undefined): string | undefined {
  const v = value?.trim();
  return v && GTIN_RE.test(v) ? v : undefined;
}

export function buildProductLd(input: BuildProductLdInput) {
  const images = (input.images ?? (input.image ? [input.image] : [])).filter(Boolean) as string[];
  const gtin = safeGtin(input.gtin);
  const mpn = input.mpn?.trim() || undefined;

  const ratings =
    input.aggregateRating && input.aggregateRating.reviewCount > 0 && input.aggregateRating.ratingValue > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: Number(input.aggregateRating.ratingValue.toFixed(2)),
            reviewCount: input.aggregateRating.reviewCount,
            bestRating: input.aggregateRating.bestRating ?? 5,
          },
        }
      : {};

  const genuineReviews = (input.reviews ?? []).filter((r) => r.author && r.ratingValue > 0);
  const reviews = genuineReviews.length
    ? {
        review: genuineReviews.map((r) => ({
          "@type": "Review",
          author: { "@type": "Person", name: r.author },
          reviewRating: {
            "@type": "Rating",
            ratingValue: r.ratingValue,
            bestRating: 5,
          },
          ...(r.body ? { reviewBody: r.body } : {}),
          ...(r.datePublished ? { datePublished: r.datePublished } : {}),
        })),
      }
    : {};

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: input.name,
    description: input.description,
    ...(images.length ? { image: images } : {}),
    ...(input.category ? { category: input.category } : {}),
    ...(input.brand ? { brand: { "@type": "Brand", name: input.brand } } : {}),
    ...(input.sku ? { sku: input.sku } : {}),
    ...(mpn ? { mpn } : {}),
    ...(gtin ? { gtin } : {}),
    ...ratings,
    ...reviews,
    ...(input.extra ?? {}),
    offers: {
      "@type": "Offer",
      url: input.url,
      priceCurrency: input.priceCurrency ?? "GBP",
      price: input.price,
      availability: input.availability,
      itemCondition: input.itemCondition ?? "https://schema.org/UsedCondition",
      ...(input.validFrom ? { validFrom: input.validFrom } : {}),
      ...(input.priceValidUntil ? { priceValidUntil: input.priceValidUntil } : {}),
      seller: { "@type": "Organization", name: input.sellerName || SITE_NAME },
      shippingDetails: offerShippingDetails(),
      hasMerchantReturnPolicy: merchantReturnPolicy(),
    },
  };
}
